/**
 * Route Definitions
 *
 * Centralized route configuration for the application.
 * Separated by public and protected routes for better organization.
 */

import { lazy } from 'react'
import type { RouteObject } from 'react-router-dom'
import ProtectedRoute from '../features/auth/components/ProtectedRoute'
import Layout from '../components/Layout'
import MapLayout from '../components/MapLayout'

// Lazy load components for code splitting
const Login = lazy(() => import('../features/auth/components/Login'))
const ForgotPassword = lazy(
  () => import('../features/auth/components/ForgotPassword')
)
const CheckEmail = lazy(() => import('../features/auth/components/CheckEmail'))
const SetPassword = lazy(
  () => import('../features/auth/components/SetPassword')
)
const PasswordResetSuccess = lazy(
  () => import('../features/auth/components/PasswordResetSuccess')
)
const AccessDenied = lazy(
  () => import('../features/auth/components/AccessDenied')
)

const MapView = lazy(() => import('../features/zones/components/MapView'))
const ZoneTable = lazy(() => import('../features/zones/components/ZoneTable'))
const MyProfile = lazy(() => import('../features/users/components/MyProfile'))
const SystemSettings = lazy(
  () => import('../features/settings/components/SystemSettings')
)
const UserManagement = lazy(
  () => import('../features/users/components/UserManagement')
)

/**
 * Public Routes (no authentication required)
 */
export const publicRoutes: RouteObject[] = [
  {
    path: '/login',
    element: <Login />,
  },
  {
    path: '/forgot-password',
    element: <ForgotPassword />,
  },
  {
    path: '/check-email',
    element: <CheckEmail />,
  },
  {
    path: '/set-password',
    element: <SetPassword />,
  },
  {
    path: '/password-reset-success',
    element: <PasswordResetSuccess />,
  },
  {
    path: '/403',
    element: <AccessDenied />,
  },
]

/**
 * Protected Routes (authentication required)
 * Map routes use MapLayout, others use simple Layout
 */
export const protectedRoutes: RouteObject[] = [
  {
    path: '/home',
    element: (
      <ProtectedRoute>
        <MapLayout>
          <MapView />
        </MapLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <MapLayout>
          <MapView />
        </MapLayout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/dashboard',
    element: (
      <ProtectedRoute>
        <Layout>
          <ZoneTable />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/profile',
    element: (
      <ProtectedRoute>
        <Layout>
          <MyProfile />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/settings',
    element: (
      <ProtectedRoute>
        <Layout>
          <SystemSettings />
        </Layout>
      </ProtectedRoute>
    ),
  },
]

/**
 * Admin-only Routes (require admin role)
 */
export const adminRoutes: RouteObject[] = [
  {
    path: '/admin/users',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <Layout>
          <UserManagement />
        </Layout>
      </ProtectedRoute>
    ),
  },
  {
    path: '/admin/assignments',
    element: (
      <ProtectedRoute requiredRoles={['admin']}>
        <Layout>
          <div className='p-8'>
            <h1 className='text-2xl font-bold mb-4'>Zone Assignments</h1>
            <p className='text-gray-600'>
              Admin-only: Zone assignment page (to be implemented)
            </p>
          </div>
        </Layout>
      </ProtectedRoute>
    ),
  },
]

/**
 * All routes combined
 */
export const appRoutes: RouteObject[] = [
  ...publicRoutes,
  ...protectedRoutes,
  ...adminRoutes,
]
